import axios from 'axios';
import { ANONYMOUS_ID_STORAGE_KEY, API_CART_INVENTORY } from '@constants';
import { AuthorizationService } from '../auth';
import { AddressDraft, CartDraft, DiscountCodeReference, LineItemDraft, MyCartDraft } from '@commercetools/platform-sdk';
import {
  Cart,
  CartAction,
  CartAddLineItemAction,
  CartChangeLineItemQuantityAction,
  CartRemoveLineItemAction,
  CartUpdateAction,
  CartSetShippingAddressAction,
  CartSetShippingMethodAction,
  CartAddDiscountCode,
  CartRemoveDiscountCode,
  CartOutOfStockItems,
  InStoreAvailabilityItem,
  FluentStoreAvailabilityResponse
} from './interfaces';
import { mapStoreAvailabilityData } from './mapper';
import { appLocalStorage, handleServiceError } from '@utils';
import { isFeatureEnabled, FEATURES } from '@utils/featureFlags';
import { API_HOST_PROXY } from '@constants/api';

export class CartService extends AuthorizationService {
  static readonly serviceName: string = 'CartService';

  public async createCart(draft: CartDraft | MyCartDraft): Promise<Cart | null> {
    try {
      const { data: cart } = await axios.post(`${API_HOST_PROXY}/carts`, { ...draft });

      if (cart?.anonymousId) {
        appLocalStorage.setItem(ANONYMOUS_ID_STORAGE_KEY, cart.anonymousId);
      }

      return cart as Cart;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  private async updateCart(cartId: string, version: number, actions: CartUpdateAction[]): Promise<Cart | null> {
    try {
      const { data: cart } = await axios.post(`${API_HOST_PROXY}/carts/${cartId}`, {
        version,
        actions
      });

      return cart as Cart;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async getMyActiveCart(storeKey?: string): Promise<Cart | null> {
    try {
      const url = new URL(`${API_HOST_PROXY}/carts/me`);
      if (isFeatureEnabled(FEATURES.MULTI_TENANCY) && storeKey) {
        url.searchParams.set('storeKey', storeKey);
      }
      const { data: cart } = await axios.get(url.toString());

      return cart ? (cart as Cart) : null;
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;

      // 404 status means the cart hasn't been created yet
      if (status !== 404) {
        handleServiceError(err);
      }

      return null;
    }
  }

  public async getCart(cartId: string): Promise<Cart | null> {
    try {
      const { data: cart } = await axios.get(`${API_HOST_PROXY}/carts/${cartId}`);

      return cart ? (cart as Cart) : null;
    } catch (err) {
      handleServiceError(err);

      return null;
    }
  }

  public async addLineItem(cartId: string, version: number, lineItemDraft: LineItemDraft): Promise<Cart | null> {
    const actions: CartAddLineItemAction[] = [ {
      action: CartAction.ADD_LINE_ITEM,
      productId: lineItemDraft.productId ?? '',
      variantId: lineItemDraft.variantId,
      sku: lineItemDraft.sku,
      quantity: lineItemDraft.quantity ?? 1
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async addDiscountCode(cartId: string, version: number, discountCode: string): Promise<Cart | null> {
    const actions: CartAddDiscountCode[] = [ {
      action: CartAction.ADD_DISCOUNT_CODE,
      code: discountCode
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async removeDiscountCode(cartId: string, version: number, discountCode: DiscountCodeReference): Promise<Cart | null> {
    const actions: CartRemoveDiscountCode[] = [ {
      action: CartAction.REMOVE_DISCOUNT_CODE,
      discountCode: { id: discountCode.id }
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async changeLineItemQuantity(
    cartId: string,
    version: number,
    lineItemId: string,
    quantity: number
  ): Promise<Cart | null> {
    const actions: CartChangeLineItemQuantityAction[] = [ {
      action: CartAction.CHANGE_LINE_ITEM_QUANTITY,
      lineItemId,
      quantity
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async removeLineItem(
    cartId: string,
    version: number,
    lineItemId: string | string[]
  ): Promise<Cart | null> {

    let actions: CartRemoveLineItemAction[] = [];

    if (typeof lineItemId === 'string') {
      actions = [ {
        action: CartAction.REMOVE_LINE_ITEM,
        lineItemId
      } ];
    } else {
      actions = lineItemId.map(item => ({ action: CartAction.REMOVE_LINE_ITEM, lineItemId: item }));
    }

    return await this.updateCart(cartId, version, actions);
  }

  public async setShippingAddress(
    cartId: string,
    version: number,
    addressDraft: Partial<AddressDraft>
  ): Promise<Cart | null> {
    const actions: CartSetShippingAddressAction[] = [ {
      action: CartAction.SET_SHIPPING_ADDRESS,
      address: {
        ...addressDraft,
        country: addressDraft.country ?? 'US'
      }
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async setShippingMethod(
    cartId: string,
    version: number,
    shippingMethodId: string
  ): Promise<Cart | null> {
    const actions: CartSetShippingMethodAction[] = [ {
      action: CartAction.SET_SHIPPING_METHOD,
      shippingMethodId
    } ];

    return await this.updateCart(cartId, version, actions);
  }

  public async checkOutOfStockCart(cartID: string, oldVersion: number):
  Promise<{unavailables: CartOutOfStockItems[], version: number}> {
    try {
      const {
        data: { data: { unavailables, version } }
      } = await axios.post(`${API_CART_INVENTORY}/unavailables`, {
        cartID,
        version: oldVersion
      });

      return { unavailables, version };
    } catch (err) {
      handleServiceError(err);

      return { unavailables: [], version: 0 };
    }
  }

  public async getInStoreAvailabilityData(sku: string, quantity: number): Promise<InStoreAvailabilityItem[]> {
    try {
      const {
        data: { data }
      } = await axios.post(`${API_CART_INVENTORY}/store-availability`, {
        products: [ {
          sku,
          quantity
        } ]
      });

      return mapStoreAvailabilityData(data as FluentStoreAvailabilityResponse, sku);
    } catch (err) {
      handleServiceError(err);

      return [];
    }
  }
}
